import React, { useState } from "react";
import { Button, ButtonToolbar, HStack, Tooltip, Whisper } from "rsuite";
import HelpOutlineIcon from "@rsuite/icons/esm/react/HelpOutline";

import { useTranslation } from "react-i18next";
import cn from "classnames";

import {
    FailureAction,
    IWorkflowStep,
    IWorkflowWithData,
} from "store/workflows/workflows-types";

import {
    hasFailedSteps,
    isInProgress,
    isInTerminated,
} from "components/Workflows/workflows-helper";

import "./Footer.less";

interface Props {
    workflow: IWorkflowWithData<IWorkflowStep, unknown>;

    onResolve: (action: FailureAction) => Promise<void>;
    onTerminate: () => Promise<void>;
    onClose?: () => void;
}

const Footer: React.FC<Props> = ({
    workflow,
    onResolve,
    onTerminate,
    onClose,
}: Props) => {
    const { t } = useTranslation();

    const [retryLoading, setRetryLoading] = useState(false);
    const [terminateLoading, setTerminateLoading] = useState(false);

    const stepFailed = hasFailedSteps(workflow.steps);
    const canTerminate = isInProgress(workflow.status);
    const terminated = isInTerminated(workflow.status);

    const handleRetry = () => {
        setRetryLoading(true);

        onResolve(FailureAction.Retry).finally(() => setRetryLoading(false));
    };

    const handleTerminate = () => {
        setTerminateLoading(true);

        onTerminate().finally(() => {
            setTerminateLoading(false);
            onClose?.();
        });
    };

    return (
        <ButtonToolbar className={"workflow-modal__footer--toolbar"}>
            {stepFailed && !terminated && (
                <HStack spacing={0}>
                    <HStack.Item alignSelf={"center"}>
                        <Button
                            className={cn("workflow-modal__footer__btn")}
                            type="submit"
                            appearance="primary"
                            onClick={handleRetry}
                            loading={retryLoading}
                        >
                            {t("workflow.footer.retry")}
                        </Button>
                    </HStack.Item>

                    <HStack.Item alignSelf="center">
                        <Whisper
                            placement="topStart"
                            trigger="hover"
                            speaker={
                                <Tooltip>
                                    {t("workflow.footer.helpText.retry")}
                                </Tooltip>
                            }
                        >
                            <HelpOutlineIcon
                                style={{
                                    marginLeft: "0.25rem",
                                    marginBottom: "0.125rem",
                                }}
                            />
                        </Whisper>
                    </HStack.Item>
                </HStack>
            )}

            <Button
                className={cn("workflow-modal__footer__btn")}
                appearance={stepFailed ? "ghost" : "primary"}
                onClick={onClose}
            >
                {t("workflow.footer.close")}
            </Button>

            {canTerminate && (
                <HStack spacing={0} style={{ marginLeft: "auto" }}>
                    <HStack.Item alignSelf={"center"}>
                        <Button
                            className={cn(
                                "workflow-modal__footer__btn",
                                "workflow-modal__footer__btn--terminate"
                            )}
                            appearance="ghost"
                            onClick={handleTerminate}
                            loading={terminateLoading}
                        >
                            {t("workflow.footer.terminate")}
                        </Button>
                    </HStack.Item>

                    <HStack.Item alignSelf="center">
                        <Whisper
                            placement="topStart"
                            trigger="hover"
                            speaker={
                                <Tooltip>
                                    {t("workflow.footer.helpText.terminate")}
                                </Tooltip>
                            }
                        >
                            <HelpOutlineIcon
                                style={{
                                    marginLeft: "0.25rem",
                                    marginBottom: "0.125rem",
                                }}
                            />
                        </Whisper>
                    </HStack.Item>
                </HStack>
            )}
        </ButtonToolbar>
    );
};

export default Footer;
